import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Button, 
    Box, 
    Card, 
    Typography, 
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    TextField,
    IconButton,
    Toolbar,
    InputAdornment} from '@mui/material';
import { 
    Add, 
    Edit, 
    Delete,
    Search,
    FilterList,
    Description
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import tenderApi from '../../../../services/tenderApi';
import { getImageUrl } from '../../../../services/api';

const TenderIndex = () => {
    const [tenders, setTenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTenders = async () => {
            try {
                const response = await tenderApi.getAll();
                console.log('Fetched tenders:', response);
                setTenders(response);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchTenders();
    }, []);

    const handleDelete = async (id) => {
        const tenderToDelete = tenders.find(tender => tender.id === id);
        
        const confirm = await Swal.fire({
            title: 'Confirm Delete',
            text: `Are you sure you want to delete the tender "${tenderToDelete.title}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
    
        if (!confirm.isConfirmed) return;
    
        try {
            await tenderApi.delete(id);
            setTenders(tenders.filter(tender => tender.id !== id));
            
            Swal.fire(
                'Deleted!',
                'Tender has been deleted successfully.',
                'success'
            );
        } catch (error) {
            console.error('Error deleting tender:', error);
            Swal.fire(
                'Error!',
                error.response?.data?.error || 'Failed to delete tender',
                'error'
            );
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const filteredTenders = tenders.filter(tender => 
        tender.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tender.description && tender.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const sortedTenders = stableSort(filteredTenders, getComparator(order, orderBy));
    const paginatedTenders = sortedTenders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

const FilePreview = ({ fileUrl, fileName }) => {
    const handleDownload = () => {
        if (!fileUrl) return;
        
        const downloadUrl = getImageUrl(fileUrl);
        const downloadFileName = fileName || fileUrl.split('/').pop();
        
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = downloadFileName;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (!fileUrl) {
        return (
            <Typography variant="body2" color="textSecondary">
                No file
            </Typography>
        );
    }

    const displayName = fileName || fileUrl.split('/').pop();

    return (
        <Box 
            onClick={handleDownload}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main'
                }
            }}
        >
            <Description fontSize="small" color="primary" />
            <Typography variant="body2">
                {displayName || 'Download'}
            </Typography>
        </Box>
    );
};

    const StatusChip = ({ closingDate }) => {
        if (!closingDate) {
            return <Chip label="Unknown" color="default" size="small" />;
        }
        
        const today = new Date();
        const closing = new Date(closingDate);
        const isOpen = closing > today;
        
        return (
            <Chip 
                label={isOpen ? 'Open' : 'Closed'} 
                color={isOpen ? 'success' : 'error'}
                size="small"
            />
        );
    };

    const columns = [
        { 
            id: 'serialNo', 
            label: '#', 
            width: '80px', 
            sortable: false,
            format: (value, row, page, rowsPerPage, index) => (
                <Typography variant="body2">
                    {(page * rowsPerPage) + index + 1}
                </Typography>
            )
        },
        { 
            id: 'title', 
            label: 'Title', 
            width: '250px', 
            sortable: true,
            format: (value) => (
                <Typography variant="subtitle2" noWrap>
                    {value}
                </Typography>
            )
        },
        { 
            id: 'description', 
            label: 'Description', 
            sortable: true,
            format: (value) => (
                <Typography variant="body2" noWrap>
                    {value || '-'}
                </Typography>
            )
        },
        { 
            id: 'closingDate', 
            label: 'Closing Date', 
            width: '150px',
            sortable: true,
            format: (value) => formatDate(value)
        },
        { 
            id: 'status', 
            label: 'Status', 
            width: '120px',
            align: 'center',
            sortable: true,
            format: (value, row) => <StatusChip closingDate={row.closingDate} />
        },
        { 
            id: 'fileUrl',
            label: 'Document', 
            width: '180px',
            sortable: false,
            format: (value, row) => (
                <FilePreview 
                    fileUrl={value} 
                    fileName={`Tender_${row.id}.pdf`}
                />
            )
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => (
                <>
                    <IconButton
                        aria-label="edit"
                        onClick={() => navigate(`/admin/tenders/${row.id}`, { state: { tenderData: row } })}
                        size="small"
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(row.id)}
                        size="small"
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between" 
                mb={3}
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}
            >
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '1.7rem' },
                        fontWeight: 600
                    }}
                >
                    Tender Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/tenders/new')}
                    sx={{ 
                        boxShadow: 'none',
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 12px', sm: '8px 16px' }
                    }}
                >
                    New Tender
                </Button>
            </Stack>

            <Card sx={{ 
                p: 2,
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                borderRadius: '12px'
            }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Toolbar
                        sx={{
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search tenders..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px'
                                }
                            }}
                        />
                        <Box sx={{ flex: '1 1 100%' }} />
                        <IconButton>
                            <FilterList />
                        </IconButton>
                    </Toolbar>
                    
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align || 'left'}
                                            width={column.width}
                                            sortDirection={orderBy === column.id ? order : false}
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                backgroundColor: 'background.paper',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {column.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === column.id}
                                                    direction={orderBy === column.id ? order : 'asc'}
                                                    onClick={() => handleRequestSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : (
                                                column.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            Loading tenders...
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedTenders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            {searchTerm ? 'No matching tenders found' : 'No tenders available'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedTenders.map((row, index) => (
                                        <TableRow
                                            hover
                                            key={row.id}
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: '1px dashed',
                                                    borderColor: 'divider'
                                                }
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell 
                                                        key={column.id}
                                                        align={column.align}
                                                    >
                                                        {column.format 
                                                            ? column.format(value, row, page, rowsPerPage, index)
                                                            : (
                                                                <Typography 
                                                                    variant="body2"
                                                                    noWrap
                                                                >
                                                                    {value || '-'}
                                                                </Typography>
                                                            )
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={filteredTenders.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Card>
        </Box>
    );
};

export default TenderIndex;